/**
 * 建议问题统一管理器
 * 整合所有建议问题相关的逻辑和数据源
 */

import { getSuggestionConfig, type SuggestionQuestion } from '@/config/contentConfig';
import { getLLMSuggestions } from '@/app/api';
import { BaseManager } from '@/lib/core/BaseManager';
import logger from '@/utils/logger';

// 建议问题来源类型
export enum SuggestionSource {
  GLOBAL_CONFIG = 'global_config',
  SCENE_CONFIG = 'scene_config',
  LLM_GENERATED = 'llm_generated',
  ENTRY_STANDBY = 'entry_standby',
  FALLBACK = 'fallback',
}

// 扩展的建议问题类型
export interface EnhancedSuggestionQuestion extends SuggestionQuestion {
  id?: string;
  source?: SuggestionSource;
  priority?: number;
  category?: string;
  keywords?: string[];
}

// 建议问题配置
interface SuggestionConfig {
  maxSuggestions: number;
  preventDuplicates: boolean;
  enableLLM: boolean;
  fallbackToPreset: boolean;
  llmTimeout: number;
}

// 对话历史类型
interface ConversationMessage {
  user: string;
  value: string;
  role?: 'user' | 'assistant';
}

class SuggestionManager extends BaseManager {
  private suggestionConfig: SuggestionConfig = {
    maxSuggestions: 2,
    preventDuplicates: true,
    enableLLM: true,
    fallbackToPreset: true,
    llmTimeout: 15000,
  };

  private lastRequestTime = 0;

  private readonly REQUEST_THROTTLE = 1000; // 1秒防抖

  constructor() {
    super({
      name: 'suggestion-manager',
    });
  }

  protected async onInit(): Promise<void> {
    logger.info('[SuggestionManager] 初始化建议问题管理器');
  }

  protected async onDestroy(): Promise<void> {
    logger.info('[SuggestionManager] 销毁建议问题管理器');
  }

  /**
   * 获取当前场景的基础问题库
   */
  private getBaseSuggestions(sceneConfig?: any): SuggestionQuestion[] {
    // 优先使用场景配置
    if (
      sceneConfig &&
      Array.isArray(sceneConfig.suggestionQuestions) &&
      sceneConfig.suggestionQuestions.length > 0
    ) {
      logger.info('[SuggestionManager] 使用场景配置问题:', sceneConfig.suggestionQuestions.length);
      return sceneConfig.suggestionQuestions;
    }

    // 回退到全局配置
    const globalSuggestions = getSuggestionConfig();
    logger.info('[SuggestionManager] 使用全局配置问题:', globalSuggestions.length);
    return globalSuggestions;
  }

  /**
   * 过滤重复问题
   */
  private filterDuplicates(
    questions: SuggestionQuestion[],
    conversationHistory?: ConversationMessage[]
  ): SuggestionQuestion[] {
    if (!this.suggestionConfig.preventDuplicates || !conversationHistory?.length) {
      return questions;
    }

    return questions.filter((question) => {
      return !conversationHistory.some((msg) => {
        if (!msg.value) return false;

        // 检查完全包含
        if (msg.value.includes(question.text) || question.text.includes(msg.value)) {
          return true;
        }

        // 检查相似度（去除标点符号后比较）
        const normalize = (text: string) =>
          text.replace(/[？?！!。.，,、：:;；"'()（）\[\]【】<>《》]/g, '').trim();

        const normalizedMsg = normalize(msg.value);
        const normalizedQuestion = normalize(question.text);

        if (normalizedMsg.length > 5 && normalizedQuestion.length > 5) {
          const similarity = Math.abs(normalizedMsg.length - normalizedQuestion.length);
          return similarity < 10 && normalizedMsg === normalizedQuestion;
        }

        return false;
      });
    });
  }

  /**
   * 高效的随机选择（Fisher-Yates洗牌算法优化版）
   */
  private shuffleAndLimit(questions: SuggestionQuestion[], maxCount: number): SuggestionQuestion[] {
    if (questions.length <= maxCount) {
      return [...questions];
    }

    const result = [...questions];
    const limit = Math.min(maxCount, result.length);

    // 只洗牌需要的数量，提高性能
    for (let i = 0; i < limit; i++) {
      const randomIndex = i + Math.floor(Math.random() * (result.length - i));
      [result[i], result[randomIndex]] = [result[randomIndex], result[i]];
    }

    return result.slice(0, limit);
  }

  /**
   * 转换为增强类型（优化版）
   */
  private enhanceQuestions(
    questions: SuggestionQuestion[],
    source: SuggestionSource,
    prefix = ''
  ): EnhancedSuggestionQuestion[] {
    return questions.map((q, index) => {
      // 只在需要时创建新对象
      if (q.id && (q as EnhancedSuggestionQuestion).source === source) {
        return q as EnhancedSuggestionQuestion;
      }

      return {
        ...q,
        id: q.id || `${prefix}${source}_${index}`,
        source,
        priority: 1,
        category: source,
      };
    });
  }

  /**
   * 获取欢迎语建议问题
   */
  async getWelcomeSuggestions(sceneConfig?: any): Promise<EnhancedSuggestionQuestion[]> {
    const baseSuggestions = this.getBaseSuggestions(sceneConfig);
    const selected = this.shuffleAndLimit(baseSuggestions, this.suggestionConfig.maxSuggestions);
    const enhanced = this.enhanceQuestions(selected, SuggestionSource.SCENE_CONFIG, 'welcome_');

    return enhanced;
  }

  /**
   * 获取对话中的建议问题（智能生成 + 预设回退）
   */
  async getConversationSuggestions(
    content: string,
    conversationHistory?: ConversationMessage[],
    sceneConfig?: any
  ): Promise<EnhancedSuggestionQuestion[]> {
    if (!content || content.length < 5) {
      return [];
    }

    // 防抖处理
    const now = Date.now();
    if (now - this.lastRequestTime < this.REQUEST_THROTTLE) {
      logger.debug('[SuggestionManager] 请求被防抖限制');
      return this.getFallbackSuggestions(sceneConfig, conversationHistory);
    }
    this.lastRequestTime = now;

    // 尝试 LLM 生成
    if (this.suggestionConfig.enableLLM) {
      try {
        const llmResult = await this.generateLLMSuggestions(content, conversationHistory);
        if (llmResult.length > 0) {
          return llmResult;
        }
      } catch (error) {
        logger.warn('[SuggestionManager] LLM 生成失败，使用预设问题:', error);
      }
    }

    // 回退到预设问题
    return this.getFallbackSuggestions(sceneConfig, conversationHistory);
  }

  /**
   * LLM 生成建议问题
   */
  private async generateLLMSuggestions(
    content: string,
    conversationHistory?: ConversationMessage[]
  ): Promise<EnhancedSuggestionQuestion[]> {
    // 构建对话历史
    const pairs: Array<{ role: 'user' | 'assistant'; content: string }> = [];
    if (conversationHistory) {
      const recent = conversationHistory.slice(-6); // 最近3轮对话
      for (const msg of recent) {
        if (!msg.value) continue;
        const role =
          msg.role || (msg.user.includes('bot') || msg.user.includes('ai') ? 'assistant' : 'user');
        pairs.push({ role, content: msg.value });
      }
    }

    const { suggestions } = await getLLMSuggestions(content, 2, undefined, pairs);
    const validSuggestions = (suggestions || []).filter(Boolean).slice(0, 2);

    return this.enhanceQuestions(
      validSuggestions.map((text) => ({ text })),
      SuggestionSource.LLM_GENERATED,
      'llm_'
    );
  }

  /**
   * 获取预设回退问题
   */
  private getFallbackSuggestions(
    sceneConfig?: any,
    conversationHistory?: ConversationMessage[]
  ): EnhancedSuggestionQuestion[] {
    const baseSuggestions = this.getBaseSuggestions(sceneConfig);
    const filtered = this.filterDuplicates(baseSuggestions, conversationHistory);
    const selected = this.shuffleAndLimit(filtered.length > 0 ? filtered : baseSuggestions, 2);

    return this.enhanceQuestions(selected, SuggestionSource.FALLBACK, 'fallback_');
  }

  /**
   * 获取入口页面建议问题
   */
  async getEntryStandbySuggestions(sceneId?: string): Promise<string[]> {
    // 默认问题
    const defaultQuestions = [
      '我们学校的历史是什么？',
      '五年级数学有哪些重点？',
      '请写一首古诗',
      '你最擅长做什么？',
    ];

    if (!sceneId) {
      return defaultQuestions;
    }

    try {
      // 动态导入避免循环依赖
      const { default: Apis } = await import('@/app/index');
      const data = await Apis.Basic.getSceneSuggestions({ scene: sceneId });

      // 兼容不同数据结构
      if (Array.isArray(data)) {
        return data.map((q: { text: string }) => q.text).filter(Boolean);
      }

      if (data?.suggestions || data?.suggestionQuestions) {
        const suggestions = data.suggestions || data.suggestionQuestions;
        if (Array.isArray(suggestions)) {
          return suggestions.map((q: any) => (typeof q === 'string' ? q : q?.text)).filter(Boolean);
        }
      }

      return defaultQuestions;
    } catch (error) {
      logger.warn('[SuggestionManager] 获取场景建议问题失败:', error);
      return defaultQuestions;
    }
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<SuggestionConfig>): void {
    this.suggestionConfig = { ...this.suggestionConfig, ...newConfig };
    logger.debug('[SuggestionManager] 配置已更新:', this.suggestionConfig);
  }

  /**
   * 获取当前配置
   */
  getSuggestionConfig(): SuggestionConfig {
    return { ...this.suggestionConfig };
  }
}

// 导出单例实例
export const suggestionManager = new SuggestionManager();

// 导出类型
export type { SuggestionConfig, ConversationMessage };

// 向后兼容的函数导出
export const getWelcomeSuggestions = (sceneConfig?: any) =>
  suggestionManager.getWelcomeSuggestions(sceneConfig);

export const getConversationSuggestions = (
  content: string,
  conversationHistory?: ConversationMessage[],
  sceneConfig?: any
) => suggestionManager.getConversationSuggestions(content, conversationHistory, sceneConfig);

export const getEntryStandbySuggestions = (sceneId?: string) =>
  suggestionManager.getEntryStandbySuggestions(sceneId);
